/**
 * Componente: VirtualizedDoctorList
 * Lista virtualizada de médicos para alta performance com milhares de registros
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { DoctorCard } from './DoctorCard';
import type { DoctorDTO } from '@/services/doctors';
import { Loader2 } from 'lucide-react';

interface VirtualizedDoctorListProps {
  doctors: DoctorDTO[];
  isLoading: boolean;
  onSelectDoctor?: (doctor: DoctorDTO) => void;
}

export function VirtualizedDoctorList({
  doctors,
  isLoading,
  onSelectDoctor,
}: VirtualizedDoctorListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Responsive columns estimation
  const [columns, setColumns] = useState(1);

  useEffect(() => {
    const updateColumns = () => {
      if (window.innerWidth >= 1280) setColumns(4); // xl
      else if (window.innerWidth >= 1024) setColumns(3); // lg
      else if (window.innerWidth >= 768) setColumns(2); // md
      else setColumns(1); // sm
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Prepare rows based on columns
  const rows = Math.ceil(doctors.length / columns);

  const estimateSize = useCallback(() => 200, []);

  // TanStack Virtualizer
  const virtualizer = useWindowVirtualizer({
    count: rows,
    estimateSize, // Estimated height of a row of cards
    overscan: 5, // Render 5 extra rows outside the viewport for smooth scrolling
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        <p className="mt-4 text-gray-500 dark:text-gray-400 font-medium">
          Buscando especialistas...
        </p>
      </div>
    );
  }

  if (doctors.length === 0) {
    return (
      <div className="text-center py-20 bg-white/50 dark:bg-neutral-900/50 rounded-2xl border border-dashed border-gray-200 dark:border-neutral-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Nenhum médico encontrado
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          Tente ajustar seus filtros de busca para ver mais resultados.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow: any) => {
          const startIndex = virtualRow.index * columns;
          const rowDoctors = (doctors || []).slice(startIndex, startIndex + columns);

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full"
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: '24px', // Gap between rows
              }}
            >
              <div 
                className="grid gap-6 w-full"
                style={{
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
                }}
              >
                {rowDoctors.map((doctor) => (
                  <div key={doctor.id} className="h-full">
                    <DoctorCard
                      doctor={doctor}
                      onSelect={onSelectDoctor}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
